<template>
  <Modal
    :modal-active="modelValue"
    has-header-border
    has-footer-border
    max-width="max-w-[720px]"
    @close="close()"
  >
    <template #header>
      <div class="text-left">
        <h2 class="text-xl font-bold text-textPrimary">Confere com a escala</h2>
        <p v-if="subtitle" class="text-xs text-neutral50 mt-0.5" data-testid="conference-subtitle">
          {{ subtitle }}
        </p>
      </div>
    </template>

    <div class="flex flex-col gap-3 text-left">
      <!-- Texto LITERAL da conferência: é o que vai para a trilha de auditoria. -->
      <div
        class="rounded-lg border border-lightGray30 border-l-4 border-l-primary30 bg-gray-50 p-3"
      >
        <p class="text-[10px] font-bold uppercase tracking-wide text-neutral50 mb-1">
          Conferência da folha do operador
        </p>
        <p class="text-sm text-textPrimary" data-testid="conference-text">
          “{{ SHEET_CONFERENCE_TEXT }}”
        </p>
      </div>

      <label
        class="flex items-start gap-2 rounded-lg border-2 border-primary30 bg-primary5/40 p-3 cursor-pointer"
      >
        <input
          v-model="signaturesChecked"
          type="checkbox"
          class="mt-0.5"
          data-testid="conference-checkbox"
        />
        <span class="text-xs">
          <span class="font-semibold">Li e confirmo a conferência da folha acima</span>
          <span class="block text-neutral50 mt-0.5">
            Obrigatório. Sem esta marcação o botão Confirmar permanece desabilitado.
          </span>
        </span>
      </label>

      <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div
          v-for="card in tallyCards"
          :key="card.outcome"
          class="rounded-lg border border-lightGray30 border-t-[3px] p-2"
          :class="card.borderClass"
          :data-testid="`conference-tally-${card.outcome}`"
        >
          <p class="text-lg font-bold leading-none" :class="card.textClass">{{ card.count }}</p>
          <p class="text-[10px] font-bold uppercase tracking-wide text-neutral50 mt-1">
            {{ card.label }}
          </p>
          <p class="text-[9px] font-mono text-neutral50">{{ card.code }}</p>
        </div>
      </div>

      <p class="text-[11px] text-neutral50">
        A linha “não consta na folha” entra na fila de divergências para tratamento no pagamento —
        não desaparece.
      </p>
    </div>

    <template #footer>
      <div class="flex flex-col sm:flex-row sm:justify-end gap-3">
        <OutlineButton
          text="Cancelar"
          width="96px"
          height="40px"
          data-testid="conference-cancel-button"
          @click="close()"
        />
        <FilledButton
          text="Confirmar conferência"
          class="bg-primary30"
          width="220px"
          height="40px"
          :disabled="!signaturesChecked || saving"
          data-testid="conference-confirm-button"
          @click="confirm()"
        />
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
/**
 * ESP-02/29-04, decisões 5 e 7 do grilling — o OK global embute UMA conferência da folha,
 * com checkbox obrigatório, e é exigido mesmo com 100% auto-conciliado pelo motor: o motor
 * confere horário × escala, o humano atesta a folha física.
 *
 * NOMENCLATURA: "Conferência da folha" / "Confirmar conferência". NUNCA "atestado" — no
 * domínio do produto isso é atestado médico.
 */
import { computed, ref, watch } from 'vue'
import Modal from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import {
  PrefillOutcome,
  PREFILL_OUTCOME_TALLY_LABELS,
  SHEET_CONFERENCE_TEXT
} from '@/constants/ds-attendance.constants'
import type { PrefillOutcomeTally } from '../utils/prefill-mirror'

const props = defineProps<{
  modelValue: boolean
  saving: boolean
  tally: PrefillOutcomeTally
  /** "Folha #5186 · 21/09/2026 · Escala Pediatria Nova · Noturno" */
  subtitle?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: []
}>()

const signaturesChecked = ref(false)

/** Reabrir o modal nunca herda a marcação anterior: a conferência é um ato por vez. */
watch(
  () => props.modelValue,
  (open) => {
    if (!open) signaturesChecked.value = false
  }
)

const TALLY_STYLES: Record<PrefillOutcome, { border: string; text: string; code: string }> = {
  [PrefillOutcome.CONFIRMED]: {
    border: 'border-t-sucess',
    text: 'text-sucess',
    code: 'CONFIRMED'
  },
  [PrefillOutcome.EDITED]: { border: 'border-t-alert', text: 'text-alert', code: 'EDITED' },
  [PrefillOutcome.ABSENT]: {
    border: 'border-t-negative',
    text: 'text-negative',
    code: 'ABSENT → DIVERGENT'
  },
  [PrefillOutcome.EXTRA]: { border: 'border-t-primary30', text: 'text-primary30', code: 'EXTRA' }
}

const tallyCards = computed(() =>
  Object.values(PrefillOutcome).map((outcome) => ({
    outcome,
    count: props.tally[outcome] ?? 0,
    label: PREFILL_OUTCOME_TALLY_LABELS[outcome],
    code: TALLY_STYLES[outcome].code,
    borderClass: TALLY_STYLES[outcome].border,
    textClass: TALLY_STYLES[outcome].text
  }))
)

function close() {
  emit('update:modelValue', false)
}

/**
 * Guarda de UI espelhando o 400 do backend: sem a conferência marcada nenhuma requisição
 * sai daqui — o botão desabilitado é a primeira barreira, esta é a segunda.
 */
function confirm() {
  if (!signaturesChecked.value) return
  emit('confirm')
}
</script>
